"use client";

import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Cpu, CircleQuestionMark as CircleHelp, Lightbulb, ShieldCheck, ArrowRight, Terminal } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
type Lang = "vi" | "en";
interface PillarItem {
  id: string;
  iconName: "HelpCircle" | "ShieldCheck" | "Cpu" | "Lightbulb";
  title: Record<Lang, string>;
  desc: Record<Lang, string>;
}
const contentData = {
  specRail: {
    vi: "Tiêu chuẩn Sư phạm // 01",
    en: "Pedagogical Standard // 01"
  },
  activeSessionBadge: {
    vi: "Buổi học Lab Thực hành",
    en: "Active Lab Session"
  },
  techStripMentor: {
    vi: "TỶ LỆ: 1 GIẢNG VIÊN / 6 HỌC VIÊN",
    en: "RATIO: 1 MENTOR / 6 STUDENTS"
  },
  techStripCore: {
    vi: "CỐT LÕI: HƯỚNG DẪN ĐẶT VẤN ĐỀ",
    en: "CORE: INQUIRY SCAFFOLDING"
  },
  headline: {
    vi: "Dạy trẻ tư duy như những chuyên gia Engineering thực thụ.",
    en: "Teaching children to think like engineers."
  },
  lead: {
    vi: "Chúng tôi đồng hành cùng học viên thông qua phương pháp đặt câu hỏi gợi mở và giải quyết vấn đề, tuyệt đối không áp đặt đáp án có sẵn.",
    en: "We guide students through inquiry and guided problem-solving, never simply handing out the answers."
  },
  description: {
    vi: "Tại ROBOTO, chương trình học được chắt lọc từ các phương pháp Engineering chuẩn đại học và tối ưu riêng cho lứa tuổi 4–16. Thay vì những bài tập lý thuyết khô khan hay đồ chơi lắp ghép thiếu nền tảng, học viên được tiếp cận Robotics, Coding và STEM bài bản: tự thiết lập giả thuyết, lắp ráp cơ điện tử thực tế và phân tích từng dòng lệnh trực tiếp.",
    en: "At ROBOTO, learning is anchored in university-level engineering disciplines adapted for young minds. Instead of rigid worksheets or drag-and-drop toys without theory, our students formulate hypotheses, assemble real electromechanical assemblies, and trace code execution line by line."
  },
  ctaTrial: {
    vi: "Đăng ký học thử miễn phí",
    en: "Book a free trial"
  },
  ctaCurriculum: {
    vi: "Xem lộ trình học tập",
    en: "View curriculum path"
  }
};
const pillars: PillarItem[] = [{
  id: "p1",
  iconName: "HelpCircle",
  title: {
    vi: "Dẫn dắt qua phương pháp gợi mở",
    en: "Inquiry-based scaffolding"
  },
  desc: {
    vi: "Giảng viên đặt các câu hỏi định hướng sắc bén, kích thích trẻ tự tìm lỗi trong Coding và làm chủ tư duy thuật toán độc lập.",
    en: "Instructors ask targeted questions that prompt students to isolate bugs and discover mathematical logic independently."
  }
}, {
  id: "p2",
  iconName: "ShieldCheck",
  title: {
    vi: "Đồng hành sát sao theo tỷ lệ nhỏ",
    en: "Close small-group mentorship"
  },
  desc: {
    vi: "Tỷ lệ 1 giảng viên : 6 học viên giúp từng em được hướng dẫn sát sao trong quá trình lắp ráp, lập trình và gỡ lỗi.",
    en: "A 1:6 mentor-to-student ratio means every child gets close guidance while building, coding, and debugging."
  }
}, {
  id: "p3",
  iconName: "Cpu",
  title: {
    vi: "Bền bỉ qua từng vòng thử nghiệm",
    en: "Resilience through iteration"
  },
  desc: {
    vi: "Trẻ xem các lần thử nghiệm thất bại là dữ liệu giá trị để cải tiến, tự tin gỡ lỗi phần cứng Robotics mà không cảm thấy nản lòng.",
    en: "Students treat failed tests as valuable diagnostic data, learning to debug mechanical systems without frustration."
  }
}, {
  id: "p4",
  iconName: "Lightbulb",
  title: {
    vi: "Lớp học Lab giới hạn chuẩn 1:6",
    en: "Small lab cohort focus"
  },
  desc: {
    vi: "Tỷ lệ tối ưu 1 mentor trên 6 học viên đảm bảo từng bạn nhỏ được đồng hành sát sao theo đúng nhịp độ và niềm đam mê STEAM.",
    en: "A strict 1:6 mentor ratio guarantees individualized coaching tailored to each child's technical pace and interests."
  }
}];
const pillarGradients = [{
  borderGradient: "from-primary via-primary/70 to-primary/20",
  iconColor: "text-primary",
  innerBg: "bg-primary/10"
}, {
  borderGradient: "from-secondary via-secondary/70 to-secondary/20",
  iconColor: "text-secondary",
  innerBg: "bg-secondary/10"
}, {
  borderGradient: "from-accent via-accent/70 to-accent/20",
  iconColor: "text-accent",
  innerBg: "bg-accent/10"
}, {
  borderGradient: "from-chart-4 via-chart-4/70 to-chart-4/20",
  iconColor: "text-chart-4",
  innerBg: "bg-chart-4/10"
}];
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes",
  alt: "ROBOTO mascot"
};
export default function Philosophy() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<Lang>("vi");
  useEffect(() => {
    const getInitialLang = (): Lang => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        return stored === "en" ? "en" : "vi";
      } catch {
        return "vi";
      }
    };
    setLang(getInitialLang());
    const handleCustomChange = (e: Event) => {
      const detail = (e as CustomEvent<{
        lang?: Lang;
      }>).detail;
      if (detail?.lang === "vi" || detail?.lang === "en") {
        setLang(detail.lang);
      } else {
        setLang(getInitialLang());
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang") {
        setLang(e.newValue === "en" ? "en" : "vi");
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-teaching-philosophy" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-24 border-b border-border overflow-hidden">
      {/* Precision Blueprint Grid Overlay */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[linear-gradient(to_right,hsl(var(--foreground))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--foreground))_1px,transparent_1px)] bg-[size:32px_32px]" />

      <div className="relative z-10 mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 xl:px-24">
        {/* Spec Rail Accent */}
        <div className="flex items-center gap-2 mb-8">
          <span className="w-1.5 h-1.5 rounded-sm bg-secondary" />
          <div className="h-px w-16 bg-secondary" />
          <span className="font-mono text-xs font-semibold tracking-widest text-secondary uppercase">
            {contentData.specRail[lang]}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Visual Column */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-6 flex flex-col">
            <div className="relative rounded-2xl border border-border bg-card p-3 shadow-md overflow-hidden">
              <AspectRatio ratio={4 / 3} className="overflow-hidden rounded-xl bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/instructor_mentoring_student_robotics_code.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/instructor_mentoring_student_robotics_code.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/instructor_mentoring_student_robotics_code.png-wvc-sizes" alt="ROBOTO robotics instructor guiding a young student through code debugging on a lab workstation" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
              </AspectRatio>

              {/* Lab Spec Indicator Badge with Circuit-line Accents */}
              <div className="absolute top-5 left-5 pointer-events-none">
                <svg className="absolute -top-3 -left-3 w-56 h-20 text-secondary opacity-35 pointer-events-none overflow-visible" viewBox="0 0 224 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  {/* Circuit line 1: left bracket trace and connection node */}
                  <path d="M 10 26 L 4 26 L 4 54 L 18 54" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="18" cy="54" r="2" fill="currentColor" />

                  {/* Circuit line 2: extending right from the badge with 45-degree trace */}
                  <path d="M 162 20 L 182 20 L 196 34 L 216 34" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="216" cy="34" r="2" fill="currentColor" />

                  {/* Circuit line 3: bottom drop trace with terminal pad */}
                  <path d="M 92 42 L 92 58 L 112 58" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="112" cy="58" r="2" fill="currentColor" />
                </svg>

                <div className="relative pointer-events-auto flex items-center gap-2 rounded-lg border border-secondary/40 bg-card/90 backdrop-blur-md px-3.5 py-1.5 shadow-sm">
                  <span className="h-2 w-2 rounded-lg bg-secondary animate-pulse" />
                  <span className="font-mono text-xs font-semibold uppercase tracking-wider text-foreground">
                    {contentData.activeSessionBadge[lang]}
                  </span>
                </div>
              </div>

              {/* Lower Technical Strip */}
              <div className="mt-3 flex items-center justify-between px-2 text-xs font-mono text-muted-foreground border-t border-border pt-3">
                <div className="flex items-center gap-1.5">
                  <Terminal className="h-3.5 w-3.5 text-secondary" />
                  <span>{contentData.techStripMentor[lang]}</span>
                </div>
                <span>{contentData.techStripCore[lang]}</span>
              </div>
            </div>
          </motion.div>

          {/* Text and Philosophy Columns */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-6 flex flex-col justify-center">
            <motion.h2 variants={itemVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
              {contentData.headline[lang]}
            </motion.h2>

            <motion.p variants={itemVariants} className="mt-4 text-lg text-muted-foreground leading-relaxed font-sans">
              {contentData.lead[lang]}
            </motion.p>

            <motion.p variants={itemVariants} className="mt-4 text-base text-foreground/80 leading-relaxed font-sans">
              {contentData.description[lang]}
            </motion.p>

            {/* Philosophy Pillars Grid */}
            <motion.div variants={itemVariants} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {pillars.map((pillar, i) => {
              const style = pillarGradients[i % 4];
              return <Card key={pillar.id} data-index={i} className="border border-border bg-card p-5 rounded-xl shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-secondary hover:shadow-md">
                    <CardContent className="p-0 flex flex-col gap-2.5">
                      <div className="flex items-center gap-3">
                        <div className={`flex h-12 w-12 shrink-0 items-center justify-center p-[1.5px] rounded-lg bg-gradient-to-br ${style.borderGradient} shadow-xs`}>
                          <div className={`flex h-full w-full items-center justify-center rounded-lg bg-card/95 backdrop-blur-xs ${style.innerBg}`}>
                            {pillar.iconName === "HelpCircle" && <CircleHelp className={`h-6 w-6 ${style.iconColor}`} />}
                            {pillar.iconName === "ShieldCheck" && <ShieldCheck className={`h-6 w-6 ${style.iconColor}`} />}
                            {pillar.iconName === "Cpu" && <Cpu className={`h-6 w-6 ${style.iconColor}`} />}
                            {pillar.iconName === "Lightbulb" && <Lightbulb className={`h-6 w-6 ${style.iconColor}`} />}
                          </div>
                        </div>
                        <h3 className="font-serif text-base font-semibold text-foreground">
                          {pillar.title[lang]}
                        </h3>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {pillar.desc[lang]}
                      </p>
                    </CardContent>
                  </Card>;
            })}
            </motion.div>

            {/* Direct Action Link */}
            <motion.div variants={itemVariants} className="mt-8 flex flex-wrap items-center gap-4 relative z-10">
              <Button asChild size="lg" className="rounded-lg bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm font-semibold">
                <Link to="/FreeTrialContactPage">
                  {contentData.ctaTrial[lang]}
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-lg border-border text-foreground hover:bg-muted font-medium">
                <Link to="/LearningPathCurriculumPage">
                  {contentData.ctaCurriculum[lang]}
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Static Floating Decorative Mascot */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.9,
      y: 12
    }} whileInView={{
      opacity: 1,
      scale: 1,
      y: 0
    }} viewport={{
      once: true,
      amount: 0.2
    }} transition={{
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }} aria-hidden="true" className="pointer-events-none select-none absolute bottom-4 right-4 sm:bottom-6 sm:right-6 lg:bottom-8 lg:right-10 xl:bottom-8 xl:right-16 z-0 hidden sm:block">
        <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 xl:w-36 xl:h-36 flex items-center justify-center bg-transparent">
          <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt="" loading="lazy" decoding="async" className="h-full w-full object-contain drop-shadow-md pointer-events-none select-none" />
        </div>
      </motion.div>
    </section>;
}